import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  Ban,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CreditCard,
  Gift,
  Infinity as InfinityIcon,
  Layers,
  Mail,
  MoreHorizontal,
  Receipt,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/Table";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/DropdownMenu";
import Button from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";
import Pagination from "../../../components/Pagination";

import {
  listPlatformBilling,
  getDriftReport,
  reconcileFromProvider,
  resendCardExpiryNotice,
  resendRenewalNotice,
  coerceAccessState,
  coerceManagementMode,
  coerceOrigin,
  coerceSubscriptionState,
  formatMoney,
  isCustomerManaged,
  type BillingAccessState,
  type BillingManagementMode,
  type BillingSubscriptionState,
  type PlatformBillingRowView,
  type PlatformDriftReportView,
  type PlatformDriftRow,
} from "../../../api/General/PlatformBilling";
import { formatDate } from "./helpers";
import {
  AccessOverrideDialog,
  BespokeDialog,
  CancelSubscriptionDialog,
  IssueCreditDialog,
  NeverExpiresDialog,
} from "./PlatformBillingDialogs";
import CompanyBillingSheet from "./CompanyBillingSheet";
import {
  PlatformAutoRenewDialog,
  PlatformManagementModeDialog,
  PlatformPlanChangeDialog,
  PlatformSeatChangeDialog,
  PlatformTermChangeDialog,
} from "./PlatformSubscriptionDialogs";

/*
 * PLATFORM BILLING (CO-08) — the Operator's view of who owes what, and where the two writers disagree.
 *
 * Design notes, so a later pass does not "improve" the deliberate parts:
 *
 * • It lives INSIDE the existing Subscriptions console and borrows its exact vocabulary — same tab
 *   strip, same max-width, same Table/Pagination primitives, same chip shape. This is a second view of
 *   the same job, not a second product. Consistency is the affordance here.
 * • The drift strip is ONE LINE at rest. It is not a KPI row and not a grid of stat cards: three
 *   healthy numbers in big type teach an Operator to stop reading it. It only expands when there is
 *   something to read.
 * • An override is disclosed on the ROW, in the access cell, as a chip plus a plain sentence beneath
 *   the company name. Not a coloured stripe, not a background wash on the row: the divergence is
 *   information, and it has to survive being scanned past.
 * • Money and seat columns are tabular-nums and end-aligned so columns compare vertically at a glance.
 * • Every mutating action is a reason-required dialog. Nothing on this page mutates on a single click.
 *
 * ⚠️ Nothing here reads employee data. "Billable seats" is the daily snapshot figure and is labelled
 * exactly that, never "Users" — the console's existing `UserCount` column is a DIFFERENT number
 * (non-archived users, ignoring employment status) and the two must never read as the same thing.
 */

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STATE_OPTIONS: { value: "" | BillingSubscriptionState; key: string }[] = [
  { value: "", key: "All billing states" },
  { value: "Active", key: "Active" },
  { value: "NonRenewing", key: "Not renewing" },
  { value: "PastDue", key: "Past due" },
  { value: "Cancelled", key: "Cancelled" },
  { value: "Bespoke", key: "Bespoke" },
  { value: "Trialing", key: "Trialing" },
  { value: "Draft", key: "Draft" },
];

/*
 * WHO MANAGES THIS SUBSCRIPTION — a first-class filter, not a detail buried in a row menu.
 *
 * The single question an operator has to answer on a support call is "can this customer change their
 * own plan?" Since the customer portal now REFUSES plan/seat/renewal/cancel changes on an
 * operator-managed subscription, getting that answer wrong sends the customer back to a screen that
 * will not let them do the thing they were just told to do.
 */
const MANAGEMENT_OPTIONS: { value: "" | BillingManagementMode; key: string }[] = [
  { value: "", key: "Managed by anyone" },
  { value: "SelfServe", key: "Managed by the customer" },
  { value: "OperatorManaged", key: "Managed by Mowazaf" },
];

/** Origin is the historical fact — how the subscription came to exist. It never changes. */
const ORIGIN_LABELS: Record<string, string> = {
  SelfServeCheckout: "Started by the customer",
  OperatorProvisioned: "Set up by Mowazaf",
  Trial: "Started as a trial",
  Migrated: "Migrated from before self-serve billing",
  Unknown: "Origin not recorded",
};

const ACCESS_OPTIONS: { value: "" | BillingAccessState; key: string }[] = [
  { value: "", key: "All access levels" },
  { value: "Full", key: "Full" },
  { value: "Grace", key: "Grace" },
  { value: "Restricted", key: "Restricted" },
  { value: "Dormant", key: "Dormant" },
];

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* ─── chips ─── */

const chipBase =
  "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider";

function stateChip(state: BillingSubscriptionState | null): string {
  switch (state) {
    case "Active":
      return `${chipBase} bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300`;
    case "PastDue":
      return `${chipBase} bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300`;
    case "Cancelled":
      return `${chipBase} bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300`;
    case "Bespoke":
      return `${chipBase} bg-primary/10 text-primary`;
    default:
      return `${chipBase} bg-muted text-muted-foreground`;
  }
}

function accessChip(access: BillingAccessState | null): string {
  switch (access) {
    case "Full":
      return `${chipBase} bg-muted text-muted-foreground`;
    case "Grace":
      return `${chipBase} bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300`;
    case "Restricted":
    case "Dormant":
      return `${chipBase} bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300`;
    default:
      return `${chipBase} bg-muted text-muted-foreground`;
  }
}

const selectClass =
  "h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary";

export default function PlatformBillingPanel() {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [state, setState] = useState<"" | BillingSubscriptionState>("");
  const [accessState, setAccessState] = useState<"" | BillingAccessState>("");
  const [managementMode, setManagementMode] = useState<"" | BillingManagementMode>("");
  const [divergedOnly, setDivergedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [rows, setRows] = useState<PlatformBillingRowView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [drift, setDrift] = useState<PlatformDriftReportView | null>(null);
  const [driftOpen, setDriftOpen] = useState(false);

  const [creditRow, setCreditRow] = useState<PlatformBillingRowView | null>(null);
  const [cancelRow, setCancelRow] = useState<PlatformBillingRowView | null>(null);
  const [bespokeRow, setBespokeRow] = useState<PlatformBillingRowView | null>(null);
  const [overrideRow, setOverrideRow] = useState<PlatformBillingRowView | null>(null);
  const [planRow, setPlanRow] = useState<PlatformBillingRowView | null>(null);
  const [seatRow, setSeatRow] = useState<PlatformBillingRowView | null>(null);
  const [termRow, setTermRow] = useState<PlatformBillingRowView | null>(null);
  const [autoRenewRow, setAutoRenewRow] = useState<PlatformBillingRowView | null>(null);
  const [managementRow, setManagementRow] = useState<PlatformBillingRowView | null>(null);
  const [perpetualRow, setPerpetualRow] = useState<PlatformBillingRowView | null>(null);

  /*
   * CO-126 — the per-company anchor. The row menu is a shortcut for someone who already knows which
   * lever they want; this is where an operator goes when the question is "what is going on with this
   * tenant". `detailToken` lets a mutation made from the row menu refresh an open sheet without
   * remounting it and losing its scroll position.
   */
  const [detailRow, setDetailRow] = useState<PlatformBillingRowView | null>(null);
  const [detailToken, setDetailToken] = useState(0);

  /*
   * CO-120 — STALE-RESPONSE GUARD. Without this, two in-flight list requests can land out of
   * order and the LATER-ARRIVING, OLDER one wins: type a filter while the unfiltered page-load
   * query is still running, the filtered result paints, then the slow unfiltered result
   * overwrites it. The panel then sits on the unfiltered page 1 with the filter text still in
   * the box and no further request pending — it never self-corrects, so it does not read as a
   * flicker, it reads as "that company does not exist".
   *
   * It gets worse as the tenant table grows (the unfiltered query is the slow one), which is
   * exactly the degradation seen from the E2E side. `SubscriptionsIndex.fetchData` has carried
   * this same `reqIdRef` guard all along; this panel was the one that did not.
   */
  const reqIdRef = useRef(0);
  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErrorMsg(null);
    try {
      const result = await listPlatformBilling({
        search: debouncedSearch,
        state,
        accessState,
        managementMode,
        divergedOnly,
        page,
        pageSize,
      });
      if (reqId !== reqIdRef.current) return;
      if (result.errors && result.errors.length > 0) {
        setErrorMsg(result.errors[0]?.description || t("Could not load billing state."));
        setRows([]);
        setTotalCount(0);
        return;
      }
      setRows(result.payload?.items ?? []);
      setTotalCount(result.payload?.totalCount ?? 0);
    } catch {
      if (reqId !== reqIdRef.current) return;
      setErrorMsg(t("Could not load billing state."));
      setRows([]);
      setTotalCount(0);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [debouncedSearch, state, accessState, managementMode, divergedOnly, page, pageSize, t]);

  const loadDrift = useCallback(async () => {
    try {
      const result = await getDriftReport();
      if (result.errors && result.errors.length > 0) return;
      setDrift(result.payload ?? null);
    } catch {
      // The drift strip is diagnostic. If it cannot load, the table is still fully usable, so this
      // must not surface as a page-level error.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadDrift();
  }, [loadDrift]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, state, accessState, managementMode, divergedOnly, pageSize]);

  const totalPages = useMemo(
    () => (pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1),
    [totalCount, pageSize],
  );

  const refreshAll = useCallback(() => {
    void load();
    void loadDrift();
    // An open sheet is showing the same subscription a row-menu dialog just changed. Leaving it
    // stale is how an operator ends up acting twice on a lever that already moved.
    setDetailToken((v) => v + 1);
  }, [load, loadDrift]);

  const runReconcile = async (row: PlatformBillingRowView) => {
    try {
      const result = await reconcileFromProvider(row.companyId);
      if (result.errors && result.errors.length > 0) {
        toast.error(result.errors[0]?.description || t("Could not reconcile."));
        return;
      }
      toast.success(t("Re-read from the provider. Nothing was charged."));
      refreshAll();
    } catch {
      toast.error(t("Could not reconcile."));
    }
  };

  /*
   * CO-130 — resend a billing notice for one row.
   *
   * 🔴 `sent === false` IS NOT AN ERROR AND MUST NOT BE TOASTED AS ONE. The server fails closed on an
   * operator-managed, Bespoke or perpetual subscription — the notice would describe a charge that will
   * never be made — and it hands back a full sentence saying so. Printing that sentence is the whole
   * point: an operator who cannot tell "refused, here is why" from "failed" will click again.
   *
   * The reason is a fixed English constant rather than a `t()` string: it is written to
   * SubscriptionAuditLog and read months later out of SQL by whoever is reconstructing the account, so
   * it must not vary with the operator's UI language. A per-resend free-text reason belongs in a small
   * dialog and is the follow-up; this ships the action.
   */
  const runResend = async (
    row: PlatformBillingRowView,
    kind: "renewal" | "card-expiry",
  ) => {
    try {
      const call = kind === "renewal" ? resendRenewalNotice : resendCardExpiryNotice;
      const result = await call(row.companyId, {
        reason: "Operator resend from the platform billing console.",
      });
      if (result.errors && result.errors.length > 0) {
        toast.error(result.errors[0]?.description || t("Could not resend the notice."));
        return;
      }
      const payload = result.payload;
      if (payload && !payload.sent) {
        toast(payload.detail || t("Not sent."));
        return;
      }
      toast.success(
        payload?.emailOnly
          ? kind === "renewal"
            ? t("Notice re-sent by email. The in-app copy was already delivered for this term.")
            : t("Notice re-sent by email. The in-app copy was already delivered for this card.")
          : t("Notice re-sent. The recorded notice date was not changed."),
      );
    } catch {
      toast.error(t("Could not resend the notice."));
    }
  };

  const driftGroups: { key: string; label: string; rows: PlatformDriftRow[] }[] = drift
    ? [
        { key: "override", label: t("Overrides holding access open"), rows: drift.overrideDivergences },
        { key: "projection", label: t("Access state not mirrored"), rows: drift.projectionMismatches },
        { key: "stuck", label: t("Payments stuck mid-flight"), rows: drift.stuckTransactions },
        { key: "webhook", label: t("Provider events never matched"), rows: drift.unattributedWebhooks },
      ].filter((g) => g.rows.length > 0)
    : [];

  return (
    <div className="space-y-5">
      {/* ── Drift strip. One line at rest; it earns space only when something is wrong. ── */}
      <section
        className="rounded-lg border border-border bg-card"
        aria-label={t("Billing drift")}
      >
        <div className="flex items-center gap-3 px-4 py-3">
          {drift && drift.totalIssues > 0 ? (
            <AlertTriangle
              size={16}
              className="shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
          ) : (
            <CheckCircle2
              size={16}
              className="shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden="true"
            />
          )}

          <p className="text-sm text-foreground min-w-0 flex-1">
            {drift === null
              ? t("Checking for drift between billing state and access…")
              : drift.totalIssues === 0
                ? t("No drift. Every tenant's access matches what they have paid for.")
                : t("{{count}} things need a look across {{groups}} checks.", {
                    count: drift.totalIssues,
                    groups: driftGroups.length,
                  })}
          </p>

          {drift !== null && drift.totalIssues > 0 && (
            <button
              type="button"
              onClick={() => setDriftOpen((v) => !v)}
              aria-expanded={driftOpen}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              {driftOpen ? t("Hide") : t("Review")}
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`transition-transform duration-200 ${driftOpen ? "rotate-180" : ""}`}
              />
            </button>
          )}
        </div>

        {driftOpen && driftGroups.length > 0 && (
          <div className="border-t border-border divide-y divide-border">
            {driftGroups.map((group) => (
              <div key={group.key} className="px-4 py-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                  <span className="ms-2 tabular-nums font-normal normal-case tracking-normal">
                    {group.rows.length}
                  </span>
                </h3>
                <ul className="mt-2 space-y-1.5">
                  {group.rows.slice(0, 20).map((r, i) => (
                    <li key={`${group.key}-${i}`} className="text-sm text-foreground">
                      {r.companyName && (
                        <span className="font-medium">
                          {r.companyName}
                          <span className="mx-1.5 text-muted-foreground/50">·</span>
                        </span>
                      )}
                      <span className="text-muted-foreground">{r.detail}</span>
                    </li>
                  ))}
                  {group.rows.length > 20 && (
                    <li className="text-xs text-muted-foreground">
                      {t("and {{count}} more", { count: group.rows.length - 20 })}
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={15}
            aria-hidden="true"
            className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 text-muted-foreground"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Search companies")}
            aria-label={t("Search companies")}
            className="h-9 w-full rounded-md border border-input bg-background ps-9 pe-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          />
        </div>

        <select
          value={state}
          onChange={(e) => setState(e.target.value as "" | BillingSubscriptionState)}
          aria-label={t("Filter by billing state")}
          className={selectClass}
        >
          {STATE_OPTIONS.map((o) => (
            <option key={o.key} value={o.value}>
              {t(o.key)}
            </option>
          ))}
        </select>

        <select
          value={accessState}
          onChange={(e) => setAccessState(e.target.value as "" | BillingAccessState)}
          aria-label={t("Filter by access level")}
          className={selectClass}
        >
          {ACCESS_OPTIONS.map((o) => (
            <option key={o.key} value={o.value}>
              {t(o.key)}
            </option>
          ))}
        </select>

        <select
          value={managementMode}
          onChange={(e) => setManagementMode(e.target.value as "" | BillingManagementMode)}
          aria-label={t("Filter by who manages the subscription")}
          className={selectClass}
        >
          {MANAGEMENT_OPTIONS.map((o) => (
            <option key={o.key} value={o.value}>
              {t(o.key)}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => setDivergedOnly((v) => !v)}
          aria-pressed={divergedOnly}
          className={
            divergedOnly
              ? "h-9 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-100 px-3 text-sm font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
              : "h-9 inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:text-foreground"
          }
        >
          <ShieldCheck size={14} aria-hidden="true" />
          {t("Override in force")}
        </button>

        <Button variant="outline" size="sm" onClick={refreshAll} className="h-9">
          <RefreshCw size={14} aria-hidden="true" className="me-1.5" />
          {t("Refresh")}
        </Button>
      </div>

      {/* ── Table ── */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : errorMsg ? (
          <div className="px-4 py-12 text-center text-sm text-destructive">{errorMsg}</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <p className="text-sm font-medium text-foreground">
              {divergedOnly
                ? t("No tenant is running on an override right now.")
                : managementMode === "OperatorManaged"
                  ? t("No subscription is managed by Mowazaf right now.")
                  : managementMode === "SelfServe"
                    ? t("No customer is managing their own subscription right now.")
                    : t("No company is on a self-serve subscription yet.")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground max-w-[52ch] mx-auto">
              {divergedOnly || managementMode
                ? t("Clear the filter to see every billing-managed tenant.")
                : t(
                    "Companies appear here the moment they complete a checkout. Until then they are managed from the Companies tab.",
                  )}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Company")}</TableHead>
                <TableHead>{t("Plan")}</TableHead>
                <TableHead>{t("Managed by")}</TableHead>
                <TableHead>{t("Billing state")}</TableHead>
                <TableHead>{t("Access")}</TableHead>
                <TableHead className="text-end">{t("Seats (paid / billable)")}</TableHead>
                <TableHead className="text-end">{t("Rate / seat")}</TableHead>
                <TableHead>{t("Term ends")}</TableHead>
                <TableHead className="text-end">{t("Outstanding")}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const rowState = coerceSubscriptionState(row.state);
                const rowAccess = coerceAccessState(row.accessState);
                // Fail-closed, mirroring the server: OperatorManaged mode OR the legacy flag OR
                // Bespoke state all mean the customer cannot self-serve.
                const customerManaged = isCustomerManaged(row);
                const rowOrigin = coerceOrigin(row.origin);
                const rowMode = coerceManagementMode(row.managementMode);
                const seatGap =
                  row.billableSeats !== null &&
                  row.billableSeats !== undefined &&
                  row.committedSeats !== null &&
                  row.committedSeats !== undefined &&
                  row.billableSeats !== row.committedSeats;

                return (
                  <TableRow key={row.companyId}>
                    <TableCell className="align-top">
                      {/*
                        The company name IS the way in. A separate "view" affordance in the row menu
                        would put the primary path eleven items down a dropdown, behind ten levers
                        that are all narrower than it.
                      */}
                      <button
                        type="button"
                        onClick={() => setDetailRow(row)}
                        className="text-start font-medium text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 rounded-sm"
                      >
                        {row.companyName}
                      </button>
                      {/* The override disclosure: a plain sentence, not a coloured stripe. */}
                      {row.isOverrideDiverged && row.accessOverrideUntilUtc && (
                        <div className="mt-1 text-xs text-amber-700 dark:text-amber-400 max-w-[46ch]">
                          {t("Access held open by an operator until {{date}}.", {
                            date: formatDate(row.accessOverrideUntilUtc),
                          })}
                          {row.operatorOverrideReason && (
                            <span className="text-muted-foreground">
                              {" "}
                              {row.operatorOverrideReason}
                            </span>
                          )}
                        </div>
                      )}
                      {/*
                        Perpetual entitlement, disclosed as its own sentence and NEVER merged with the
                        override sentence above it. An override expires; this does not, and an operator
                        who reads them as the same thing will wait for a lapse that is never coming.
                      */}
                      {row.neverExpires && (
                        <div className="mt-1 text-xs text-muted-foreground max-w-[46ch]">
                          {/*
                            "Set to never expire ON <date>" put a DATE immediately after the word
                            "expire", which is the one place a date must never sit on a row about a
                            subscription that has no end date. It read as the expiry. The fact stated
                            first, the grant metadata second.
                          */}
                          {row.neverExpiresSetByName
                            ? t("Never expires. Granted by {{name}}, {{date}}.", {
                                name: row.neverExpiresSetByName,
                                date: formatDate(row.neverExpiresSetAtUtc),
                              })
                            : t("Never expires. Granted {{date}}.", {
                                date: formatDate(row.neverExpiresSetAtUtc),
                              })}
                          {row.neverExpiresReason && <> {row.neverExpiresReason}</>}
                        </div>
                      )}
                    </TableCell>

                    <TableCell className="align-top">
                      <span className="text-sm text-muted-foreground">{row.planCode ?? "—"}</span>
                    </TableCell>

                    {/*
                      "Managed by" answers the triage question in one glance, and the sub-line says
                      HOW the subscription started — the fact that settles "but we never signed up
                      for this". Origin is deliberately quieter than mode: mode is actionable today,
                      origin is history.
                    */}
                    <TableCell className="align-top">
                      <span
                        className={
                          customerManaged
                            ? `${chipBase} bg-muted text-muted-foreground`
                            : `${chipBase} bg-primary/10 text-primary`
                        }
                        title={row.managementReason ?? undefined}
                      >
                        {customerManaged ? t("Customer") : t("Mowazaf")}
                      </span>
                      {rowOrigin && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {t(ORIGIN_LABELS[rowOrigin] ?? "Origin not recorded")}
                        </div>
                      )}
                      {!customerManaged && rowMode === null && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {t("Inferred from the billing state.")}
                        </div>
                      )}
                    </TableCell>

                    <TableCell className="align-top">
                      <span className={stateChip(rowState)}>{t(rowState ?? "Unknown")}</span>
                    </TableCell>

                    <TableCell className="align-top">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className={accessChip(rowAccess)}>{t(rowAccess ?? "Unknown")}</span>
                        {row.isOverrideDiverged && (
                          <span
                            className={`${chipBase} bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-300`}
                            title={row.operatorOverrideReason ?? undefined}
                          >
                            <ShieldCheck size={10} aria-hidden="true" />
                            {t("Override")}
                          </span>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="align-top text-end tabular-nums">
                      {/*
                        An em-dash where a number belongs reads as a broken join. It is not: billable
                        seats come from BillingSeatSnapshots, which the 02:00 UTC metering job writes,
                        so a company provisioned today genuinely has nothing to show until tomorrow.
                        Say that instead of printing a dash and letting the operator guess.
                      */}
                      <span className="text-foreground">{row.committedSeats ?? "—"}</span>
                      {row.billableSeats != null ? (
                        <>
                          <span className="mx-1 text-muted-foreground/50">/</span>
                          <span
                            className={
                              seatGap ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"
                            }
                          >
                            {row.billableSeats}
                          </span>
                        </>
                      ) : (
                        <div className="mt-0.5 text-xs font-normal text-muted-foreground">
                          {t("Billable seats are counted overnight.")}
                        </div>
                      )}
                    </TableCell>

                    <TableCell className="align-top text-end tabular-nums text-muted-foreground">
                      {formatMoney(row.effectiveRatePerSeatPerMonth, row.currency)}
                    </TableCell>

                    {/*
                      The at-a-glance signal. A perpetual tenant reads as "never" in the one column an
                      operator scans to find who is about to lapse — the term date is still shown
                      underneath, because the commercial term keeps running even though it stops
                      costing the customer their access.
                    */}
                    <TableCell className="align-top text-sm text-muted-foreground">
                      {row.neverExpires ? (
                        <>
                          <span className={`${chipBase} bg-primary/10 text-primary`}>
                            <InfinityIcon size={10} aria-hidden="true" />
                            {t("Never")}
                          </span>
                          {/*
                            The label matters here. Stacking "Term ends <date>" directly under a
                            "Never" chip is a flat self-contradiction in a single cell, and the column
                            an operator scans for lapses is the worst place to put one. The commercial
                            term genuinely does keep running; saying so removes the contradiction
                            without hiding the date.
                          */}
                          {row.termEndUtc && (
                            <div className="mt-1 text-xs">
                              {t("Commercial term runs to {{date}}", {
                                date: formatDate(row.termEndUtc),
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        formatDate(row.termEndUtc)
                      )}
                    </TableCell>

                    <TableCell className="align-top text-end tabular-nums">
                      <span
                        className={
                          row.outstandingGross > 0
                            ? "font-medium text-amber-700 dark:text-amber-400"
                            : "text-muted-foreground"
                        }
                      >
                        {formatMoney(row.outstandingGross, row.currency)}
                      </span>
                    </TableCell>

                    <TableCell className="align-top">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label={t("Actions for {{company}}", { company: row.companyName })}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                          >
                            <MoreHorizontal size={16} aria-hidden="true" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailRow(row)}>
                            <Receipt size={14} aria-hidden="true" className="me-2" />
                            {t("Invoices, tax and history")}
                          </DropdownMenuItem>
                          {/*
                            The commercial levers first — these are what an operator reaches for on a
                            call. They exist because the customer portal now refuses these same five
                            actions on a subscription we manage; without them, taking a subscription
                            over would mean nobody could change it.
                          */}
                          <DropdownMenuItem onClick={() => setPlanRow(row)}>
                            <Layers size={14} aria-hidden="true" className="me-2" />
                            {t("Change plan")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSeatRow(row)}>
                            <Users size={14} aria-hidden="true" className="me-2" />
                            {t("Change committed seats")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTermRow(row)}>
                            <CalendarClock size={14} aria-hidden="true" className="me-2" />
                            {t("Change the term")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setAutoRenewRow(row)}>
                            <RefreshCw size={14} aria-hidden="true" className="me-2" />
                            {row.autoRenew ? t("Turn off auto-renew") : t("Turn on auto-renew")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setManagementRow(row)}>
                            <RotateCcw size={14} aria-hidden="true" className="me-2" />
                            {customerManaged
                              ? t("Take over this subscription")
                              : t("Hand back to the customer")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setCreditRow(row)}>
                            <Gift size={14} aria-hidden="true" className="me-2" />
                            {t("Issue account credit")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPerpetualRow(row)}>
                            <InfinityIcon size={14} aria-hidden="true" className="me-2" />
                            {row.neverExpires
                              ? t("Resume normal expiry")
                              : t("Keep running indefinitely")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setOverrideRow(row)}>
                            <ShieldCheck size={14} aria-hidden="true" className="me-2" />
                            {t("Grant full access temporarily")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setBespokeRow(row)}>
                            <Building2 size={14} aria-hidden="true" className="me-2" />
                            {row.operatorManaged
                              ? t("Return to self-serve billing")
                              : t("Convert to manual invoicing")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => void runReconcile(row)}>
                            <RefreshCw size={14} aria-hidden="true" className="me-2" />
                            {t("Re-read from provider")}
                          </DropdownMenuItem>
                          {/*
                            Both resends are offered on EVERY row, including the operator-managed and
                            perpetual ones. That is deliberate: the server decides, says why, and the
                            refusal is printed. Hiding the item would move a money-bearing policy
                            decision into the menu, where it cannot be audited.
                          */}
                          <DropdownMenuItem onClick={() => void runResend(row, "renewal")}>
                            <Mail size={14} aria-hidden="true" className="me-2" />
                            {t("Resend renewal notice")}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => void runResend(row, "card-expiry")}>
                            <CreditCard size={14} aria-hidden="true" className="me-2" />
                            {t("Resend card-expiry notice")}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setCancelRow(row)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Ban size={14} aria-hidden="true" className="me-2" />
                            {t("Cancel subscription")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {!loading && !errorMsg && rows.length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={setPageSize}
          totalCount={totalCount}
        />
      )}

      <IssueCreditDialog
        open={creditRow !== null}
        onOpenChange={(o) => !o && setCreditRow(null)}
        row={creditRow}
        onSuccess={() => {
          toast.success(t("Credit issued. It applies to the next invoice automatically."));
          refreshAll();
        }}
      />
      <CancelSubscriptionDialog
        open={cancelRow !== null}
        onOpenChange={(o) => !o && setCancelRow(null)}
        row={cancelRow}
        onSuccess={() => {
          toast.success(t("Subscription cancelled. The refund has been calculated and recorded."));
          refreshAll();
        }}
      />
      <BespokeDialog
        open={bespokeRow !== null}
        onOpenChange={(o) => !o && setBespokeRow(null)}
        row={bespokeRow}
        onSuccess={() => {
          toast.success(t("Billing mode updated."));
          refreshAll();
        }}
      />
      <PlatformPlanChangeDialog
        open={planRow !== null}
        onOpenChange={(o) => !o && setPlanRow(null)}
        row={planRow}
        onSuccess={() => {
          toast.success(t("Plan changed. The reason is on the subscription history."));
          refreshAll();
        }}
      />
      <PlatformSeatChangeDialog
        open={seatRow !== null}
        onOpenChange={(o) => !o && setSeatRow(null)}
        row={seatRow}
        onSuccess={() => {
          toast.success(t("Committed seats updated."));
          refreshAll();
        }}
      />
      <PlatformTermChangeDialog
        open={termRow !== null}
        onOpenChange={(o) => !o && setTermRow(null)}
        row={termRow}
        onSuccess={() => {
          toast.success(t("Term updated."));
          refreshAll();
        }}
      />
      <PlatformAutoRenewDialog
        open={autoRenewRow !== null}
        onOpenChange={(o) => !o && setAutoRenewRow(null)}
        row={autoRenewRow}
        onSuccess={() => {
          toast.success(t("Auto-renew updated."));
          refreshAll();
        }}
      />
      <PlatformManagementModeDialog
        open={managementRow !== null}
        onOpenChange={(o) => !o && setManagementRow(null)}
        row={managementRow}
        onSuccess={() => {
          toast.success(t("Updated who manages this subscription."));
          refreshAll();
        }}
      />
      <NeverExpiresDialog
        open={perpetualRow !== null}
        onOpenChange={(o) => !o && setPerpetualRow(null)}
        row={perpetualRow}
        onSuccess={() => {
          toast.success(
            perpetualRow?.neverExpires
              ? t("Back on the normal schedule. Renewal and dunning apply again.")
              : t("This company will not expire. Only an operator can end it."),
          );
          refreshAll();
        }}
      />
      <CompanyBillingSheet
        open={detailRow !== null}
        onOpenChange={(o) => !o && setDetailRow(null)}
        row={detailRow}
        refreshToken={detailToken}
        onMutated={() => {
          void load();
          void loadDrift();
        }}
      />

      <AccessOverrideDialog
        open={overrideRow !== null}
        onOpenChange={(o) => !o && setOverrideRow(null)}
        row={overrideRow}
        onSuccess={() => {
          toast.success(t("Access granted. It expires on its own and stays flagged until then."));
          refreshAll();
        }}
      />
    </div>
  );
}
